/**
 * useStripeSetupIntent
 *
 * Step 1 of the Stripe client-confirm save flow: fetches a SetupIntent
 * `{ clientSecret, publishableKey }` for the user's HOME club while the payment
 * form is active. The caller mounts the unified Payment Element with
 * `publishableKey` and confirms via `stripe.confirmSetup({ elements, … })`.
 *
 * The instrument type is NOT chosen here — the server resolves the intent's
 * `payment_method_types` (card, or card + bank for the accordion), and the
 * element renders whatever it enabled.
 *
 * Provider-agnostic shell: the actual `POST /setup-intent` call is INJECTED via
 * `createSetupIntent` (each app has its own API client), so this package never
 * imports an app service.
 *
 * Single-fetch by design: creating a SetupIntent creates/reuses a Stripe
 * customer server-side, so firing it twice (StrictMode double-invoke,
 * double-click) can race customer creation and bind the intent to a different
 * customer than the one persisted. `requestedKeyRef` dedupes to one request per
 * `(userId, retryNonce)` AND acts as a staleness guard so only the current
 * request sets state. There is deliberately **no cleanup cancellation**: under
 * StrictMode that would cancel the only in-flight request while the re-run skips
 * re-fetching, leaving the form stuck.
 *
 * @module hooks/payment/useStripeSetupIntent
 */

import { useCallback, useEffect, useRef, useState } from 'react'

/** The SetupIntent essentials the browser needs to confirm a card. */
export interface StripeSetupIntentValue {
  clientSecret: string
  /** The user's HOME club's Stripe publishable key — load Elements with exactly this. */
  publishableKey: string
}

export interface UseStripeSetupIntentOptions<TId extends string | number> {
  /**
   * App-injected `POST /setup-intent` call. Only `clientSecret` +
   * `publishableKey` are read; extra response fields are ignored.
   */
  createSetupIntent: (userId: TId) => Promise<{ clientSecret?: string; publishableKey?: string }>
}

export interface UseStripeSetupIntentResult {
  setupIntent: StripeSetupIntentValue | null
  isLoading: boolean
  error: string | null
  /** Force a fresh SetupIntent (e.g. after a transient failure or a consumed intent). */
  retry: () => void
}

/**
 * Fetch a Stripe SetupIntent for `userId` while `enabled` is true.
 *
 * `userId` is used only as the dedupe key and passed to `createSetupIntent`, so
 * it stays the app's native type (`number` for web/mobile, `string` for admin) —
 * do not coerce here or the strict-equality dedupe breaks.
 */
export function useStripeSetupIntent<TId extends string | number>(
  userId: TId | undefined,
  enabled: boolean,
  { createSetupIntent }: UseStripeSetupIntentOptions<TId>
): UseStripeSetupIntentResult {
  const [setupIntent, setSetupIntent] = useState<StripeSetupIntentValue | null>(null)
  // Start loading when we'll fetch on mount, so there's no one-frame blank.
  const [isLoading, setIsLoading] = useState<boolean>(enabled && userId != null)
  const [error, setError] = useState<string | null>(null)
  const [retryNonce, setRetryNonce] = useState(0)

  // Dedupe + staleness key, `${userId}#${retryNonce}`. A retry bumps the nonce,
  // changing the key so the effect re-fetches even though userId/enabled didn't.
  const requestedKeyRef = useRef<string | null>(null)
  // Latest injected fetcher, held in a ref so its per-render identity isn't a dep.
  const createSetupIntentRef = useRef(createSetupIntent)
  createSetupIntentRef.current = createSetupIntent

  const retry = useCallback(() => {
    requestedKeyRef.current = null
    // Drop the old intent BEFORE refetching. A retry is only ever called because the
    // previous intent was spent (client confirm succeeded, server persist failed), so
    // leaving it populated during the refetch leaves a fully-mounted, enabled form over
    // a consumed clientSecret — a second click confirms against it and fails. Nulling it
    // makes admin show "Preparing…" and mobile/web2 unmount the form (which also
    // disposes the Payment Element's stale isReady/isComplete).
    setSetupIntent(null)
    setRetryNonce((n) => n + 1)
  }, [])

  useEffect(() => {
    // State updates live inside this async function (not the synchronous effect
    // body) so they run as microtasks, avoiding cascading renders.
    const run = async () => {
      if (!enabled || userId == null) {
        // Reset when disabled so re-enabling fetches a fresh single-use intent.
        requestedKeyRef.current = null
        setSetupIntent(null)
        setError(null)
        setIsLoading(false)
        return
      }

      const key = `${userId}#${retryNonce}`
      // Already requested for this (user, retry) — don't create a second intent.
      if (requestedKeyRef.current === key) return
      requestedKeyRef.current = key

      setIsLoading(true)
      setError(null)

      try {
        const res = await createSetupIntentRef.current(userId)
        // Superseded by a newer request (user changed / retry)? Discard.
        if (requestedKeyRef.current !== key) return
        if (!res.clientSecret || !res.publishableKey) {
          setError('Unable to start payment setup. Please try again.')
          return
        }
        setSetupIntent({ clientSecret: res.clientSecret, publishableKey: res.publishableKey })
      } catch (err) {
        // Keep `requestedKeyRef.current === key` so `finally` still clears loading
        // (nulling it here would make the guard false → stuck spinner) and a stale
        // rejection can't wipe a newer in-flight request's guard. `retry()` re-fires
        // on its own — it nulls the ref AND bumps the nonce.
        if (requestedKeyRef.current === key) {
          setError(err instanceof Error ? err.message : 'Unable to start payment setup.')
        }
      } finally {
        if (requestedKeyRef.current === key) setIsLoading(false)
      }
    }

    void run()
  }, [enabled, userId, retryNonce])

  return { setupIntent, isLoading, error, retry }
}
